import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Checkbox,
  Badge,
  InputAdornment,
  InputLabel,
  IconButton,
  MenuItem,
  Menu,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  CircleDollarSign,
  FolderKanban,
  Info,
  Phone,
  RefreshCcw,
  Search,
  UserRoundSearch,
  Users,
  UserCheck2,
  X
} from 'lucide-react';
import { alpha, styled } from '@mui/material/styles';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { toast } from 'react-toastify';
import { useJob } from '../context/JobContext';
import {
  getImportFunnelixContattiJobStatus,
  getFunnelixContatti,
  getFunnelixContattiDistincts,
  getFunnelixContattiPipelineSummary,
  getFunnelixContattiStageSummary,
  getFunnelixPipeline,
  getFunnelixStage,
  getFunnelixKpi,
  getFunnelixLogSync,
  getEstimateImportFunnelixContatti,
  getLastSuccessfulImportFunnelixContatti,
  refreshFunnelixPipeline,
  startImportFunnelixContattiJob,
  startPushClientiFunnelixJob,
  startVerificaFunnelixClientiJob
} from '../services/funnelixApi';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const HeroKpiCard = ({ title, value, subtitle, loading, icon: Icon, iconBg, iconColor }) => (
  <StyledCard>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {Icon ? (
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: iconBg || '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon size={18} color={iconColor || '#4f46e5'} />
          </Box>
        ) : null}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {loading ? '-' : value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    </CardContent>
  </StyledCard>
);

const formatNumber = (value) => {
  if (value == null) return '-';
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('it-IT');
};

const formatPercent = (value, decimals = 1) => {
  if (value == null) return '-';
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0%';
  return `${n.toFixed(decimals).replace('.', ',')}%`;
};

const formatCurrency = (value, currency = 'EUR') => {
  if (value == null) return '-';
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2
  }).format(n);
};

export default function FunnelixPage() {
  const { addJob } = useJob();
  const today = useMemo(() => new Date(), []);
  const currentYear = today.getFullYear();
  const defaultDataInserimentoDal = `${currentYear}-01-01`;
  const defaultDataInserimentoAl = `${currentYear}-${String(today.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(today.getDate()).padStart(2, '0')}`;
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importJobId, setImportJobId] = useState(null);
  const [importJobState, setImportJobState] = useState(null);
  const [importJobProgress, setImportJobProgress] = useState(0);
  const [importJobError, setImportJobError] = useState('');
  const importPollingTimeoutRef = useRef(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importMode, setImportMode] = useState('incrementale');
  const [importDateFrom, setImportDateFrom] = useState('');
  const [importDateTo, setImportDateTo] = useState('');
  const [importPreset, setImportPreset] = useState('ultimi_7_giorni');
  const [lastSuccessfulImportAt, setLastSuccessfulImportAt] = useState(null);
  const [incrementalFromAt, setIncrementalFromAt] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimatedContacts, setEstimatedContacts] = useState(null);
  const [estimateError, setEstimateError] = useState('');
  const [estimateFromAt, setEstimateFromAt] = useState(null);
  const [estimateToAt, setEstimateToAt] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [pushingClienti, setPushingClienti] = useState(false);
  const [pushClientiLastUpdateLoading, setPushClientiLastUpdateLoading] = useState(false);
  const [pushClientiLastUpdate, setPushClientiLastUpdate] = useState({
    aggiornato_su_funnelix_il: null,
    aggiornato_su_funnelix_da_nome_utente: null
  });
  const [clientiVerificatiTabLastUpdateLoading, setClientiVerificatiTabLastUpdateLoading] = useState(false);
  const [clientiVerificatiTabLastUpdate, setClientiVerificatiTabLastUpdate] = useState({
    aggiornato_su_funnelix_il: null,
    aggiornato_su_funnelix_da_nome_utente: null
  });
  const [verificaDialogOpen, setVerificaDialogOpen] = useState(false);
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  const [pushClientiDialogOpen, setPushClientiDialogOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [pipelineOptions, setPipelineOptions] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [pipelineSummary, setPipelineSummary] = useState({ total: 0, items: [] });
  const [stageSummary, setStageSummary] = useState({ total: 0, items: [] });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('ultimo_sync');
  const [sortDir, setSortDir] = useState('desc');
  const [activeTab, setActiveTab] = useState('contatti');
  const [filters, setFilters] = useState({
    search: '',
    stato_verifica_cliente: '',
    cliente_trovato: '',
    pipeline: '',
    stage: '',
    data_inserimento_dal: defaultDataInserimentoDal,
    data_inserimento_al: defaultDataInserimentoAl,
    aggiornato_su_funnelix_dal: '',
    aggiornato_su_funnelix_al: '',
    includi_mai_aggiornati_funnelix: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    funnelixContactId: false,
    funnelixDateAdded: true,
    funnelixDateUpdated: true
  });
  const [logsLoading, setLogsLoading] = useState(false);
  const [logRows, setLogRows] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10);
  const [logsFilters, setLogsFilters] = useState({
    search: '',
    esito: '',
    entita_tipo: ''
  });
  const [heroLoading, setHeroLoading] = useState(false);
  const [heroMetrics, setHeroMetrics] = useState({
    totaleContatti: 0,
    matchRate: 0,
    syncErrorRate: null,
    valoreClienti: null
  });

  const savedVerificationFiltersRef = useRef(null);

  const normalizePipelineOptions = useCallback((items = []) => {
    const map = new Map();
    for (const item of items || []) {
      const pipelineId =
        item?.funnelix_pipeline_id ||
        item?.pipeline_id ||
        item?.pipelineId ||
        item?.id ||
        '';
      if (!pipelineId) continue;
      const nome = item?.nome || item?.name || item?.pipeline_nome || String(pipelineId);
      if (!map.has(String(pipelineId))) {
        map.set(String(pipelineId), {
          funnelix_pipeline_id: String(pipelineId),
          nome
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a?.nome || '').localeCompare(String(b?.nome || ''), 'it', { sensitivity: 'base' })
    );
  }, []);

  const normalizeStageOptions = useCallback((items = []) => {
    const map = new Map();
    for (const item of items || []) {
      const pipelineId =
        item?.funnelix_pipeline_id ||
        item?.pipeline_id ||
        item?.pipelineId ||
        item?.pipeline ||
        '';
      const stageId =
        item?.funnelix_stage_id ||
        item?.stage_id ||
        item?.pipelineStageId ||
        item?.id ||
        '';
      if (!stageId) continue;
      const nome = item?.nome || item?.name || item?.stage_nome || String(stageId);
      const key = `${String(pipelineId || '')}::${String(stageId)}`;
      if (!map.has(key)) {
        map.set(key, {
          funnelix_pipeline_id: pipelineId ? String(pipelineId) : null,
          funnelix_stage_id: String(stageId),
          nome
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a?.nome || '').localeCompare(String(b?.nome || ''), 'it', { sensitivity: 'base' })
    );
  }, []);

  const localPipelineOptions = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      const pipelineId = row?.funnelix_pipeline_id;
      if (!pipelineId) continue;
      if (!map.has(pipelineId)) {
        map.set(pipelineId, {
          funnelix_pipeline_id: pipelineId,
          nome: row?.pipeline_nome || pipelineId
        });
      }
    }
    return normalizePipelineOptions(Array.from(map.values()));
  }, [normalizePipelineOptions, rows]);

  const localStageOptions = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      const pipelineId = row?.funnelix_pipeline_id;
      const stageId = row?.funnelix_stage_id;
      if (!pipelineId || !stageId) continue;
      if (filters.pipeline && String(pipelineId) !== String(filters.pipeline)) continue;
      const key = `${pipelineId}::${stageId}`;
      if (!map.has(key)) {
        map.set(key, {
          funnelix_pipeline_id: pipelineId,
          funnelix_stage_id: stageId,
          nome: row?.stage_nome || stageId
        });
      }
    }
    return normalizeStageOptions(Array.from(map.values()));
  }, [filters.pipeline, normalizeStageOptions, rows]);

  const isClientiVerificatiTab = activeTab === 'clienti_verificati';

  useEffect(() => {
    if (activeTab === 'clienti_verificati') {
      if (!savedVerificationFiltersRef.current) {
        savedVerificationFiltersRef.current = {
          stato_verifica_cliente: filters.stato_verifica_cliente,
          cliente_trovato: filters.cliente_trovato
        };
      }
      setFilters((prev) => ({
        ...prev,
        stato_verifica_cliente: 'verificato_cliente',
        cliente_trovato: '1'
      }));
      return;
    }

    if (savedVerificationFiltersRef.current) {
      const saved = savedVerificationFiltersRef.current;
      savedVerificationFiltersRef.current = null;
      setFilters((prev) => ({
        ...prev,
        stato_verifica_cliente: saved.stato_verifica_cliente ?? '',
        cliente_trovato: saved.cliente_trovato ?? ''
      }));
    }
  }, [activeTab]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const effectiveStatoVerifica = isClientiVerificatiTab
        ? 'verificato_cliente'
        : (filters.stato_verifica_cliente || undefined);
      const effectiveClienteTrovato = isClientiVerificatiTab
        ? '1'
        : (filters.cliente_trovato === '' ? undefined : filters.cliente_trovato);
      const effectiveAggiornatoDal = isClientiVerificatiTab
        ? (filters.aggiornato_su_funnelix_dal || undefined)
        : undefined;
      const effectiveAggiornatoAl = isClientiVerificatiTab
        ? (filters.aggiornato_su_funnelix_al || undefined)
        : undefined;
      const effectiveIncludiMaiAggiornati = isClientiVerificatiTab
        ? (filters.includi_mai_aggiornati_funnelix ? '1' : undefined)
        : undefined;
      const response = await getFunnelixContatti({
        page: page + 1,
        limit: rowsPerPage,
        search: filters.search || undefined,
        stato_verifica_cliente: effectiveStatoVerifica,
        pipeline: filters.pipeline || undefined,
        stage: filters.stage || undefined,
        data_inserimento_dal: filters.data_inserimento_dal || undefined,
        data_inserimento_al: filters.data_inserimento_al || undefined,
        aggiornato_su_funnelix_dal: effectiveAggiornatoDal,
        aggiornato_su_funnelix_al: effectiveAggiornatoAl,
        includi_mai_aggiornati_funnelix: effectiveIncludiMaiAggiornati,
        sortBy,
        sortDir,
        cliente_trovato: effectiveClienteTrovato
      });

      setRows(response.data || []);
      setTotal(Number(response.total || 0));
    } catch (error) {
      toast.error('Errore nel caricamento contatti Funnelix');
    } finally {
      setLoading(false);
    }
  }, [
    filters.cliente_trovato,
    filters.pipeline,
    filters.stage,
    filters.data_inserimento_dal,
    filters.data_inserimento_al,
    filters.aggiornato_su_funnelix_dal,
    filters.aggiornato_su_funnelix_al,
    filters.includi_mai_aggiornati_funnelix,
    filters.search,
    filters.stato_verifica_cliente,
    page,
    rowsPerPage,
    sortBy,
    sortDir,
    isClientiVerificatiTab
  ]);

  const loadDistinctOptions = useCallback(async () => {
    try {
      const effectiveStatoVerifica = isClientiVerificatiTab
        ? 'verificato_cliente'
        : (filters.stato_verifica_cliente || undefined);
      const effectiveClienteTrovato = isClientiVerificatiTab
        ? '1'
        : (filters.cliente_trovato === '' ? undefined : filters.cliente_trovato);
      const effectiveAggiornatoDal = isClientiVerificatiTab
        ? (filters.aggiornato_su_funnelix_dal || undefined)
        : undefined;
      const effectiveAggiornatoAl = isClientiVerificatiTab
        ? (filters.aggiornato_su_funnelix_al || undefined)
        : undefined;
      const effectiveIncludiMaiAggiornati = isClientiVerificatiTab
        ? (filters.includi_mai_aggiornati_funnelix ? '1' : undefined)
        : undefined;
      const response = await getFunnelixContattiDistincts({
        search: filters.search || undefined,
        stato_verifica_cliente: effectiveStatoVerifica,
        cliente_trovato: effectiveClienteTrovato,
        data_inserimento_dal: filters.data_inserimento_dal || undefined,
        data_inserimento_al: filters.data_inserimento_al || undefined,
        aggiornato_su_funnelix_dal: effectiveAggiornatoDal,
        aggiornato_su_funnelix_al: effectiveAggiornatoAl,
        includi_mai_aggiornati_funnelix: effectiveIncludiMaiAggiornati,
        pipeline: filters.pipeline || undefined
      });

      const nextPipelines = normalizePipelineOptions(
        Array.isArray(response?.pipelines) ? response.pipelines : []
      );
      const nextStages = normalizeStageOptions(
        Array.isArray(response?.stages) ? response.stages : []
      );
      const mergedPipelines = [
        ...nextPipelines,
        ...localPipelineOptions.filter(
          (p) =>
            !nextPipelines.some(
              (x) => String(x?.funnelix_pipeline_id) === String(p?.funnelix_pipeline_id)
            )
        )
      ];
      const mergedStages = [
        ...nextStages,
        ...localStageOptions.filter(
          (s) =>
            !nextStages.some(
              (x) =>
                String(x?.funnelix_pipeline_id) === String(s?.funnelix_pipeline_id) &&
                String(x?.funnelix_stage_id) === String(s?.funnelix_stage_id)
            )
        )
      ];
      const finalPipelines = normalizePipelineOptions(
        mergedPipelines.length ? mergedPipelines : localPipelineOptions
      );
      const finalStages = normalizeStageOptions(
        mergedStages.length ? mergedStages : localStageOptions
      );
      if (finalPipelines.length || finalStages.length) {
        setPipelineOptions(finalPipelines);
        setStageOptions(finalStages);
      } else {
        let pipelineCatalogData = [];
        let stageCatalogData = [];
        try {
          const pipelineCatalog = await getFunnelixPipeline();
          pipelineCatalogData = Array.isArray(pipelineCatalog?.data) ? pipelineCatalog.data : [];
        } catch (_error) {
          pipelineCatalogData = [];
        }
        try {
          const stageCatalog = await getFunnelixStage({
            pipeline_id: filters.pipeline || undefined
          });
          stageCatalogData = Array.isArray(stageCatalog?.data) ? stageCatalog.data : [];
        } catch (_error) {
          stageCatalogData = [];
        }
        setPipelineOptions(normalizePipelineOptions(pipelineCatalogData));
        setStageOptions(normalizeStageOptions(stageCatalogData));
      }
      if (
        filters.stage &&
        !finalStages.some((s) => String(s?.funnelix_stage_id) === String(filters.stage))
      ) {
        setFilters((prev) => ({
          ...prev,
          stage: ''
        }));
      }
    } catch (_error) {
      setPipelineOptions(normalizePipelineOptions(localPipelineOptions));
      setStageOptions(normalizeStageOptions(localStageOptions));
    }
  }, [
    filters.search,
    filters.stato_verifica_cliente,
    filters.cliente_trovato,
    filters.data_inserimento_dal,
    filters.data_inserimento_al,
    filters.aggiornato_su_funnelix_dal,
    filters.aggiornato_su_funnelix_al,
    filters.includi_mai_aggiornati_funnelix,
    filters.pipeline,
    filters.stage,
    isClientiVerificatiTab,
    localPipelineOptions,
    localStageOptions,
    normalizePipelineOptions,
    normalizeStageOptions
  ]);

  const loadPipelineSummary = useCallback(async () => {
    try {
      const effectiveAggiornatoDal = isClientiVerificatiTab
        ? (filters.aggiornato_su_funnelix_dal || undefined)
        : undefined;
      const effectiveAggiornatoAl = isClientiVerificatiTab
        ? (filters.aggiornato_su_funnelix_al || undefined)
        : undefined;
      const effectiveIncludiMaiAggiornati = isClientiVerificatiTab
        ? (filters.includi_mai_aggiornati_funnelix ? '1' : undefined)
        : undefined;
      const response = await getFunnelixContattiPipelineSummary({
        search: filters.search || undefined,
        stato_verifica_cliente: isClientiVerificatiTab
          ? 'verificato_cliente'
          : (filters.stato_verifica_cliente || undefined),
        cliente_trovato: isClientiVerificatiTab
          ? '1'
          : (filters.cliente_trovato === '' ? undefined : filters.cliente_trovato),
        pipeline: filters.pipeline || undefined,
        stage: filters.stage || undefined,
        data_inserimento_dal: filters.data_inserimento_dal || undefined,
        data_inserimento_al: filters.data_inserimento_al || undefined,
        aggiornato_su_funnelix_dal: effectiveAggiornatoDal,
        aggiornato_su_funnelix_al: effectiveAggiornatoAl,
        includi_mai_aggiornati_funnelix: effectiveIncludiMaiAggiornati
      });
      setPipelineSummary({
        total: Number(response?.total || 0),
        items: Array.isArray(response?.items) ? response.items : []
      });
    } catch (_error) {
      setPipelineSummary({ total: 0, items: [] });
    }
  }, [
    filters.search,
    filters.stato_verifica_cliente,
    filters.cliente_trovato,
    filters.pipeline,
    filters.stage,
    filters.data_inserimento_dal,
    filters.data_inserimento_al,
    filters.aggiornato_su_funnelix_dal,
    filters.aggiornato_su_funnelix_al,
    filters.includi_mai_aggiornati_funnelix,
    isClientiVerificatiTab
  ]);

  const loadStageSummary = useCallback(async () => {
    try {
      const effectiveAggiornatoDal = isClientiVerificatiTab
        ? (filters.aggiornato_su_funnelix_dal || undefined)
        : undefined;
      const effectiveAggiornatoAl = isClientiVerificatiTab
        ? (filters.aggiornato_su_funnelix_al || undefined)
        : undefined;
      const effectiveIncludiMaiAggiornati = isClientiVerificatiTab
        ? (filters.includi_mai_aggiornati_funnelix ? '1' : undefined)
        : undefined;
      const response = await getFunnelixContattiStageSummary({
        search: filters.search || undefined,
        stato_verifica_cliente: isClientiVerificatiTab
          ? 'verificato_cliente'
          : (filters.stato_verifica_cliente || undefined),
        cliente_trovato: isClientiVerificatiTab
          ? '1'
          : (filters.cliente_trovato === '' ? undefined : filters.cliente_trovato),
        pipeline: filters.pipeline || undefined,
        stage: filters.stage || undefined,
        data_inserimento_dal: filters.data_inserimento_dal || undefined,
        data_inserimento_al: filters.data_inserimento_al || undefined,
        aggiornato_su_funnelix_dal: effectiveAggiornatoDal,
        aggiornato_su_funnelix_al: effectiveAggiornatoAl,
        includi_mai_aggiornati_funnelix: effectiveIncludiMaiAggiornati
      });
      setStageSummary({
        total: Number(response?.total || 0),
        items: Array.isArray(response?.items) ? response.items : []
      });
    } catch (_error) {
      setStageSummary({ total: 0, items: [] });
    }
  }, [
    filters.search,
    filters.stato_verifica_cliente,
    filters.cliente_trovato,
    filters.pipeline,
    filters.stage,
    filters.data_inserimento_dal,
    filters.data_inserimento_al,
    filters.aggiornato_su_funnelix_dal,
    filters.aggiornato_su_funnelix_al,
    filters.includi_mai_aggiornati_funnelix,
    isClientiVerificatiTab
  ]);

  const formatYmdToIt = useCallback((ymd) => {
    if (!ymd) return '-';
    const [y, m, d] = String(ymd).split('-');
    if (!y || !m || !d) return ymd;
    return `${d}/${m}/${y}`;
  }, []);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (!isClientiVerificatiTab && filters.stato_verifica_cliente) count += 1;
    if (!isClientiVerificatiTab && filters.cliente_trovato !== '') count += 1;
    if (filters.pipeline) count += 1;
    if (filters.stage) count += 1;
    if (filters.data_inserimento_dal) count += 1;
    if (filters.data_inserimento_al) count += 1;
    if (isClientiVerificatiTab && filters.aggiornato_su_funnelix_dal) count += 1;
    if (isClientiVerificatiTab && filters.aggiornato_su_funnelix_al) count += 1;
    if (isClientiVerificatiTab && filters.includi_mai_aggiornati_funnelix) count += 1;
    return count;
  }, [
    filters.cliente_trovato,
    filters.pipeline,
    filters.stage,
    filters.stato_verifica_cliente,
    filters.data_inserimento_dal,
    filters.data_inserimento_al,
    filters.aggiornato_su_funnelix_dal,
    filters.aggiornato_su_funnelix_al,
    filters.includi_mai_aggiornati_funnelix,
    isClientiVerificatiTab
  ]);

  const selectedPipelineLabel = useMemo(() => {
    if (!filters.pipeline) return '';
    const selected = pipelineOptions.find(
      (p) => String(p?.funnelix_pipeline_id) === String(filters.pipeline)
    );
    return selected?.nome || filters.pipeline;
  }, [filters.pipeline, pipelineOptions]);

  const selectedStageLabel = useMemo(() => {
    if (!filters.stage) return '';
    const selected = stageOptions.find(
      (s) => String(s?.funnelix_stage_id) === String(filters.stage)
    );
    return selected?.nome || filters.stage;
  }, [filters.stage, stageOptions]);

  const pipelineCountMap = useMemo(() => {
    const map = new Map();
    for (const item of pipelineSummary.items || []) {
      const key = item?.funnelix_pipeline_id ? String(item.funnelix_pipeline_id) : '__NONE__';
      map.set(key, Number(item?.totale || 0));
    }
    return map;
  }, [pipelineSummary.items]);

  const pipelineOptionLabel = useCallback((pipeline) => {
    const pipelineId = pipeline?.funnelix_pipeline_id ? String(pipeline.funnelix_pipeline_id) : '__NONE__';
    const count = pipelineCountMap.get(pipelineId);
    const baseLabel = pipeline?.nome || pipeline?.funnelix_pipeline_id || 'Senza pipeline';
    return Number.isFinite(count) ? `${baseLabel} (${count})` : baseLabel;
  }, [pipelineCountMap]);

  const stageCountMap = useMemo(() => {
    const map = new Map();
    for (const item of stageSummary.items || []) {
      const pipelineId = item?.funnelix_pipeline_id ? String(item.funnelix_pipeline_id) : '__NONE__';
      const stageId = item?.funnelix_stage_id ? String(item.funnelix_stage_id) : '__NONE__';
      map.set(`${pipelineId}::${stageId}`, Number(item?.totale || 0));
      map.set(stageId, Number(item?.totale || 0));
    }
    return map;
  }, [stageSummary.items]);

  const stageOptionLabel = useCallback((stage) => {
    const pipelineId = stage?.funnelix_pipeline_id ? String(stage.funnelix_pipeline_id) : '__NONE__';
    const stageId = stage?.funnelix_stage_id ? String(stage.funnelix_stage_id) : '__NONE__';
    const key = `${pipelineId}::${stageId}`;
    const count = stageCountMap.has(key) ? stageCountMap.get(key) : stageCountMap.get(stageId);
    const baseLabel = stage?.nome || stage?.funnelix_stage_id || 'Senza stage';
    return Number.isFinite(count) ? `${baseLabel} (${count})` : baseLabel;
  }, [stageCountMap]);

  const clearFilterField = useCallback((field) => {
    if (field === 'search') {
      setSearchInput('');
    }
    setFilters((prev) => ({
      ...prev,
      [field]: field === 'includi_mai_aggiornati_funnelix' ? false : ''
    }));
    setPage(0);
  }, []);

  const clearFilterFieldFromEvent = useCallback((event, field) => {
    event.preventDefault();
    event.stopPropagation();
    clearFilterField(field);
  }, [clearFilterField]);

  const loadLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const response = await getFunnelixLogSync({
        page: logsPage + 1,
        limit: logsRowsPerPage,
        search: logsFilters.search || undefined,
        esito: logsFilters.esito || undefined,
        entita_tipo: logsFilters.entita_tipo || undefined
      });
      setLogRows(response.data || []);
      setLogsTotal(Number(response.total || 0));
    } catch (error) {
      toast.error('Errore nel caricamento log Funnelix');
    } finally {
      setLogsLoading(false);
    }
  }, [logsFilters.entita_tipo, logsFilters.esito, logsFilters.search, logsPage, logsRowsPerPage]);

  const loadHeroMetrics = useCallback(async () => {
    try {
      setHeroLoading(true);
      const effectiveStatoVerifica = isClientiVerificatiTab
        ? 'verificato_cliente'
        : (filters.stato_verifica_cliente || undefined);
      const effectiveClienteTrovato = isClientiVerificatiTab
        ? '1'
        : (filters.cliente_trovato === '' ? undefined : filters.cliente_trovato);

      const baseParams = {
        search: filters.search || undefined,
        stato_verifica_cliente: effectiveStatoVerifica,
        cliente_trovato: effectiveClienteTrovato,
        pipeline: filters.pipeline || undefined,
        stage: filters.stage || undefined,
        data_inserimento_dal: filters.data_inserimento_dal || undefined,
        data_inserimento_al: filters.data_inserimento_al || undefined
      };

      const [contattiClienti, valoreClientiRows] = await Promise.all([
        getFunnelixContatti({
          page: 1,
          limit: 1,
          ...baseParams,
          cliente_trovato: '1'
        }),
        getFunnelixKpi({
          period: 'last_12_months',
          metric: 'revenue',
          group_by: 'total',
          stato_verifica_cliente: 'verificato_cliente',
          pipeline: filters.pipeline || undefined,
          stage: filters.stage || undefined,
          data_inserimento_dal: filters.data_inserimento_dal || undefined,
          data_inserimento_al: filters.data_inserimento_al || undefined
        })
      ]);

      const totaleContatti = Number(total || 0);
      const totaleClienti = Number(contattiClienti?.total || 0);
      const matchRate = totaleContatti > 0 ? (totaleClienti / totaleContatti) * 100 : 0;

      const valoreClienti = Number(valoreClientiRows?.[0]?.value || 0);

      setHeroMetrics({
        totaleContatti,
        matchRate,
        syncErrorRate: null,
        valoreClienti: Number.isFinite(valoreClienti) ? valoreClienti : null
      });
    } catch (_error) {
      setHeroMetrics({
        totaleContatti: 0,
        matchRate: 0,
        syncErrorRate: null,
        valoreClienti: null
      });
    } finally {
      setHeroLoading(false);
    }
  }, [
    filters.search,
    filters.stato_verifica_cliente,
    filters.cliente_trovato,
    filters.pipeline,
    filters.stage,
    filters.data_inserimento_dal,
    filters.data_inserimento_al,
    isClientiVerificatiTab,
    total
  ]);

  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput || '';
      if (next === (filters.search || '')) return;
      setFilters((prev) => ({
        ...prev,
        search: next
      }));
      setPage(0);
    }, 550);
    return () => clearTimeout(timer);
  }, [filters.search, searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadData();
    }, 250);
    return () => clearTimeout(timer);
  }, [loadData]);

  useEffect(() => {
    if (!showFilters && !filters.pipeline && !filters.stage) return undefined;
    const timer = setTimeout(() => {
      loadDistinctOptions();
    }, 250);
    return () => clearTimeout(timer);
  }, [loadDistinctOptions, showFilters, filters.pipeline, filters.stage]);

  useEffect(() => {
    if (!showFilters) return undefined;
    const timer = setTimeout(() => {
      loadPipelineSummary();
    }, 250);
    return () => clearTimeout(timer);
  }, [loadPipelineSummary, showFilters]);

  useEffect(() => {
    if (!showFilters) return undefined;
    const timer = setTimeout(() => {
      loadStageSummary();
    }, 250);
    return () => clearTimeout(timer);
  }, [loadStageSummary, showFilters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadHeroMetrics();
    }, 250);
    return () => clearTimeout(timer);
  }, [loadHeroMetrics]);

  useEffect(() => {
    setPage(0);
  }, [
    filters.search,
    filters.stato_verifica_cliente,
    filters.cliente_trovato,
    filters.pipeline,
    filters.stage,
    filters.data_inserimento_dal,
    filters.data_inserimento_al,
    filters.aggiornato_su_funnelix_dal,
    filters.aggiornato_su_funnelix_al,
    filters.includi_mai_aggiornati_funnelix
  ]);

  useEffect(() => {
    if (activeTab !== 'log') return undefined;
    const timer = setTimeout(() => {
      loadLogs();
    }, 250);
    return () => clearTimeout(timer);
  }, [activeTab, loadLogs]);

  const clearImportPolling = useCallback(() => {
    if (importPollingTimeoutRef.current) {
      clearTimeout(importPollingTimeoutRef.current);
      importPollingTimeoutRef.current = null;
    }
  }, []);

  const pollImportJobStatus = useCallback(
    async (jobId) => {
      try {
        const status = await getImportFunnelixContattiJobStatus(jobId);
        setImportJobState(status.state || null);
        setImportJobProgress(Number(status.progress || 0));

        if (status.state === 'completed') {
          setImporting(false);
          setImportDialogOpen(false);
          setImportJobError('');
          await loadData();
          return;
        }

        if (status.state === 'failed') {
          const errMessage = status.error || 'Job import Funnelix fallito';
          setImportJobError(errMessage);
          setImporting(false);
          return;
        }

        importPollingTimeoutRef.current = setTimeout(() => {
          pollImportJobStatus(jobId);
        }, 1800);
      } catch (error) {
        const errMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Errore polling stato job import';
        setImportJobError(errMessage);
        toast.error(errMessage);
        setImporting(false);
      }
    },
    [loadData]
  );

  const handleImport = async (payload = {}) => {
    try {
      clearImportPolling();
      setImporting(true);
      setImportJobError('');
      setImportJobProgress(0);
      setImportJobState('waiting');
      const result = await startImportFunnelixContattiJob(payload);
      const jobId = result.job_id;
      setImportJobId(jobId);
      addJob(jobId, 'funnelix_import_sync', { silent: true });
      toast.info('Import Funnelix avviato correttamente. Ti aggiorniamo al completamento nelle notifiche.');
      // Chiude subito il modal: il job continua in background.
      setImportDialogOpen(false);
      setImporting(false);
      await pollImportJobStatus(jobId);
    } catch (error) {
      const message =
        error?.response?.data?.error || error?.response?.data?.message || 'Errore import contatti';
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  const handleConfirmImport = async () => {
    const payload = {
      mode: importMode
    };

    if (importMode === 'intervallo') {
      if (!importDateFrom || !importDateTo) {
        toast.error('Seleziona entrambe le date per la modalità intervallo');
        return;
      }
      payload.date_from = `${importDateFrom}T00:00:00.000Z`;
      payload.date_to = `${importDateTo}T23:59:59.999Z`;
    }

    await handleImport(payload);
  };

  const toYmd = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const normalizeDateInputToYmd = (value) => {
    const raw = String(value || '').trim().slice(0, 10);
    if (!raw) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(raw)) return raw.replaceAll('/', '-');
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      const [dd, mm, yyyy] = raw.split('/');
      return `${yyyy}-${mm}-${dd}`;
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const [dd, mm, yyyy] = raw.split('-');
      return `${yyyy}-${mm}-${dd}`;
    }
    return '';
  };

  const applyImportPreset = useCallback((preset) => {
    const now = new Date();
    const end = new Date(now);
    const start = new Date(now);

    if (preset === 'oggi') {
      // start/end remain today
    } else if (preset === 'ultimi_7_giorni') {
      start.setDate(start.getDate() - 6);
    } else if (preset === 'ultimo_mese') {
      start.setMonth(start.getMonth() - 1);
    }

    setImportDateFrom(toYmd(start));
    setImportDateTo(toYmd(end));
  }, []);

  useEffect(() => {
    if (importMode !== 'intervallo') return;
    if (importPreset === 'custom') return;
    applyImportPreset(importPreset);
  }, [applyImportPreset, importMode, importPreset]);

  useEffect(() => {
    return () => {
      clearImportPolling();
    };
  }, [clearImportPolling]);

  const executeVerifica = async () => {
    try {
      setVerifying(true);
      const includeDateFrom =
        filters.data_inserimento_dal &&
        String(filters.data_inserimento_dal) !== String(defaultDataInserimentoDal);
      const includeDateTo =
        filters.data_inserimento_al &&
        String(filters.data_inserimento_al) !== String(defaultDataInserimentoAl);
      const result = await startVerificaFunnelixClientiJob({
        search: filters.search || undefined,
        stato_verifica_cliente: filters.stato_verifica_cliente || undefined,
        cliente_trovato: filters.cliente_trovato === '' ? undefined : filters.cliente_trovato,
        pipeline: filters.pipeline || undefined,
        stage: filters.stage || undefined,
        data_inserimento_dal: includeDateFrom ? filters.data_inserimento_dal : undefined,
        data_inserimento_al: includeDateTo ? filters.data_inserimento_al : undefined
      });
      addJob(result.job_id, 'funnelix_verifica_clienti_csv', { silent: true });
      toast.info('Verifica clienti avviata. Il CSV sarà disponibile nelle notifiche al completamento.');
      setVerificaDialogOpen(false);
    } catch (error) {
      const message =
        error?.response?.data?.error || error?.response?.data?.message || 'Errore verifica clienti';
      toast.error(message);
    } finally {
      setVerifying(false);
    }
  };

  const executeRefreshCache = async () => {
    try {
      setRefreshingCache(true);
      const result = await refreshFunnelixPipeline();
      toast.success(
        `Cache aggiornata: pipeline ${result.pipeline_aggiornate || 0}, stage ${
          result.stage_aggiornati || 0
        }. Usalo quando modifichi pipeline/stage in Funnelix.`
      );
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Errore refresh cache pipeline';
      toast.error(message);
    } finally {
      setRefreshingCache(false);
    }
  };

  const executePushClienti = async () => {
    try {
      setPushingClienti(true);
      const includeDateFrom =
        filters.data_inserimento_dal &&
        String(filters.data_inserimento_dal) !== String(defaultDataInserimentoDal);
      const includeDateTo =
        filters.data_inserimento_al &&
        String(filters.data_inserimento_al) !== String(defaultDataInserimentoAl);
      const result = await startPushClientiFunnelixJob({
        search: filters.search || undefined,
        pipeline: filters.pipeline || undefined,
        stage: filters.stage || undefined,
        data_inserimento_dal: includeDateFrom ? filters.data_inserimento_dal : undefined,
        data_inserimento_al: includeDateTo ? filters.data_inserimento_al : undefined,
        aggiornato_su_funnelix_dal: filters.aggiornato_su_funnelix_dal || undefined,
        aggiornato_su_funnelix_al: filters.aggiornato_su_funnelix_al || undefined,
        includi_mai_aggiornati_funnelix: filters.includi_mai_aggiornati_funnelix ? 1 : undefined
      });
      addJob(result.job_id, 'funnelix_push_clienti');
      setPushClientiDialogOpen(false);
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Errore aggiornamento clienti su Funnelix';
      toast.error(message);
    } finally {
      setPushingClienti(false);
    }
  };

  useEffect(() => {
    if (!pushClientiDialogOpen) return;
    let cancelled = false;

    const loadLastUpdate = async () => {
      try {
        setPushClientiLastUpdateLoading(true);
        const includeDateFrom =
          filters.data_inserimento_dal &&
          String(filters.data_inserimento_dal) !== String(defaultDataInserimentoDal);
        const includeDateTo =
          filters.data_inserimento_al &&
          String(filters.data_inserimento_al) !== String(defaultDataInserimentoAl);

        const response = await getFunnelixContatti({
          page: 1,
          limit: 1,
          search: filters.search || undefined,
          stato_verifica_cliente: 'verificato_cliente',
          cliente_trovato: '1',
          pipeline: filters.pipeline || undefined,
          stage: filters.stage || undefined,
          data_inserimento_dal: includeDateFrom ? filters.data_inserimento_dal : undefined,
          data_inserimento_al: includeDateTo ? filters.data_inserimento_al : undefined,
          aggiornato_su_funnelix_dal: filters.aggiornato_su_funnelix_dal || undefined,
          aggiornato_su_funnelix_al: filters.aggiornato_su_funnelix_al || undefined,
          sortBy: 'aggiornato_su_funnelix_il',
          sortDir: 'desc'
        });

        const row = Array.isArray(response?.data) ? response.data[0] : null;
        if (cancelled) return;
        setPushClientiLastUpdate({
          aggiornato_su_funnelix_il: row?.aggiornato_su_funnelix_il || null,
          aggiornato_su_funnelix_da_nome_utente: row?.aggiornato_su_funnelix_da_nome_utente || null
        });
      } catch (_error) {
        if (cancelled) return;
        setPushClientiLastUpdate({
          aggiornato_su_funnelix_il: null,
          aggiornato_su_funnelix_da_nome_utente: null
        });
      } finally {
        if (!cancelled) setPushClientiLastUpdateLoading(false);
      }
    };

    loadLastUpdate();

    return () => {
      cancelled = true;
    };
  }, [
    pushClientiDialogOpen,
    defaultDataInserimentoAl,
    defaultDataInserimentoDal,
    filters.data_inserimento_al,
    filters.data_inserimento_dal,
    filters.aggiornato_su_funnelix_al,
    filters.aggiornato_su_funnelix_dal,
    filters.pipeline,
    filters.search,
    filters.stage
  ]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('it-IT');
  };

  const formatTempoFa = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 45 * 1000) return 'poco fa';
    const diffMin = Math.floor(diffMs / (60 * 1000));
    if (diffMin < 60) return diffMin === 1 ? '1 minuto fa' : `${diffMin} minuti fa`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return diffH === 1 ? '1 ora fa' : `${diffH} ore fa`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 30) return diffD === 1 ? '1 giorno fa' : `${diffD} giorni fa`;
    const diffM = Math.floor(diffD / 30);
    if (diffM < 12) return diffM === 1 ? '1 mese fa' : `${diffM} mesi fa`;
    const diffY = Math.floor(diffM / 12);
    return diffY === 1 ? '1 anno fa' : `${diffY} anni fa`;
  };

  const formatDateTimeRome = useCallback((value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return new Intl.DateTimeFormat('it-IT', {
      timeZone: 'Europe/Rome',
      dateStyle: 'short',
      timeStyle: 'medium'
    }).format(d);
  }, []);

  const formatDateTimeShortRome = useCallback((value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return new Intl.DateTimeFormat('it-IT', {
      timeZone: 'Europe/Rome',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
      .format(d)
      .replace(',', '')
      .trim();
  }, []);

  useEffect(() => {
    if (activeTab !== 'clienti_verificati') return;
    let cancelled = false;

    const loadTabLastUpdate = async () => {
      try {
        setClientiVerificatiTabLastUpdateLoading(true);
        const response = await getFunnelixContatti({
          page: 1,
          limit: 1,
          search: filters.search || undefined,
          stato_verifica_cliente: 'verificato_cliente',
          cliente_trovato: '1',
          pipeline: filters.pipeline || undefined,
          stage: filters.stage || undefined,
          data_inserimento_dal: filters.data_inserimento_dal || undefined,
          data_inserimento_al: filters.data_inserimento_al || undefined,
          sortBy: 'aggiornato_su_funnelix_il',
          sortDir: 'desc'
        });

        const row = Array.isArray(response?.data) ? response.data[0] : null;
        if (cancelled) return;
        setClientiVerificatiTabLastUpdate({
          aggiornato_su_funnelix_il: row?.aggiornato_su_funnelix_il || null,
          aggiornato_su_funnelix_da_nome_utente: row?.aggiornato_su_funnelix_da_nome_utente || null
        });
      } catch (_error) {
        if (cancelled) return;
        setClientiVerificatiTabLastUpdate({
          aggiornato_su_funnelix_il: null,
          aggiornato_su_funnelix_da_nome_utente: null
        });
      } finally {
        if (!cancelled) setClientiVerificatiTabLastUpdateLoading(false);
      }
    };

    loadTabLastUpdate();

    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    filters.data_inserimento_al,
    filters.data_inserimento_dal,
    filters.pipeline,
    filters.search,
    filters.stage
  ]);

  const clientiVerificatiTabLabel = useMemo(() => {
    const base = 'Clienti Verificati';
    if (clientiVerificatiTabLastUpdateLoading && !clientiVerificatiTabLastUpdate.aggiornato_su_funnelix_il) {
      return base;
    }
    if (!clientiVerificatiTabLastUpdate.aggiornato_su_funnelix_il) return base;

    const when = formatDateTimeShortRome(clientiVerificatiTabLastUpdate.aggiornato_su_funnelix_il);
    const who = clientiVerificatiTabLastUpdate.aggiornato_su_funnelix_da_nome_utente
      ? ` da ${clientiVerificatiTabLastUpdate.aggiornato_su_funnelix_da_nome_utente}`
      : '';
    return `${base} (ult.agg su Funnelix ${when}${who})`;
  }, [
    clientiVerificatiTabLastUpdate.aggiornato_su_funnelix_da_nome_utente,
    clientiVerificatiTabLastUpdate.aggiornato_su_funnelix_il,
    clientiVerificatiTabLastUpdateLoading,
    formatDateTimeShortRome
  ]);

  const formatDateRomeOnly = useCallback((yyyyMmDd, endOfDay = false) => {
    if (!yyyyMmDd) return '-';
    const iso = endOfDay
      ? `${yyyyMmDd}T23:59:59.999Z`
      : `${yyyyMmDd}T00:00:00.000Z`;
    return formatDateTimeRome(iso);
  }, [formatDateTimeRome]);

  const incrementaleDaLabel = useMemo(() => {
    if (!incrementalFromAt) return 'Nessun import precedente (prima esecuzione)';
    return formatDateTimeRome(incrementalFromAt);
  }, [formatDateTimeRome, incrementalFromAt]);

  const intervalloEffettivoLabel = useMemo(() => {
    if (importMode === 'intervallo') {
      const fromLabel = formatDateRomeOnly(importDateFrom, false);
      const toLabel = formatDateRomeOnly(importDateTo, true);
      return `${fromLabel} - ${toLabel}`;
    }
    const fromLabel = lastSuccessfulImportAt
      ? formatDateTimeRome(lastSuccessfulImportAt)
      : 'inizio disponibile';
    const toLabel = formatDateTimeRome(new Date().toISOString());
    return `${fromLabel} - ${toLabel}`;
  }, [formatDateRomeOnly, formatDateTimeRome, importDateFrom, importDateTo, importMode, lastSuccessfulImportAt]);

  const getStatoChip = (stato) => {
    if (stato === 'verificato_cliente') return <Chip size="small" color="success" label="Cliente" />;
    if (stato === 'verificato_non_cliente') {
      return <Chip size="small" color="default" label="Non cliente" />;
    }
    if (stato === 'errore') return <Chip size="small" color="error" label="Errore" />;
    return <Chip size="small" color="warning" label="Da verificare" />;
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  const tableColumnCount = isClientiVerificatiTab
    ? (visibleColumns.funnelixContactId ? 1 : 0) + 7
    : (visibleColumns.funnelixContactId ? 1 : 0) +
      (visibleColumns.funnelixDateAdded ? 1 : 0) +
      (visibleColumns.funnelixDateUpdated ? 1 : 0) +
      8;
  const contactTableMinWidth = isClientiVerificatiTab
    ? Math.max(900, tableColumnCount * 185)
    : Math.max(1300, tableColumnCount * 165);

  const loadLastSuccessfulImport = useCallback(async () => {
    try {
      const response = await getLastSuccessfulImportFunnelixContatti();
      setLastSuccessfulImportAt(response?.last_successful_import_at || null);
      setIncrementalFromAt(
        response?.incremental_from_at ||
        response?.last_successful_import_at ||
        null
      );
    } catch (_error) {
      setLastSuccessfulImportAt(null);
      setIncrementalFromAt(null);
    }
  }, []);

  const loadImportEstimate = useCallback(async () => {
    if (!importDialogOpen) return;
    if (importMode === 'intervallo' && (!importDateFrom || !importDateTo)) {
      setEstimatedContacts(null);
      setEstimateError('');
      setEstimateFromAt(null);
      setEstimateToAt(null);
      return;
    }
    try {
      setEstimateLoading(true);
      setEstimateError('');
      const result = await getEstimateImportFunnelixContatti({
        mode: importMode,
        date_from: importMode === 'intervallo' ? importDateFrom : undefined,
        date_to: importMode === 'intervallo' ? importDateTo : undefined
      });
      setEstimatedContacts(
        Number.isFinite(Number(result?.total_available))
          ? Number(result.total_available)
          : null
      );
      setEstimateFromAt(result?.from_at || null);
      setEstimateToAt(result?.to_at || null);
    } catch (error) {
      setEstimatedContacts(null);
      setEstimateFromAt(null);
      setEstimateToAt(null);
      setEstimateError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Stima momentaneamente non disponibile'
      );
    } finally {
      setEstimateLoading(false);
    }
  }, [importDateFrom, importDateTo, importDialogOpen, importMode]);

  useEffect(() => {
    if (!importDialogOpen) return;
    loadLastSuccessfulImport();
  }, [importDialogOpen, loadLastSuccessfulImport]);

  useEffect(() => {
    if (!importDialogOpen) return;
    const timer = setTimeout(() => {
      loadImportEstimate();
    }, 300);
    return () => clearTimeout(timer);
  }, [importDialogOpen, importMode, importDateFrom, importDateTo, loadImportEstimate]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FolderKanban size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Funnelix
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contatti importati da Funnelix e stato verifica clienti
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={refreshingCache ? <CircularProgress size={16} /> : <RefreshCcw size={16} />}
            onClick={() => setRefreshDialogOpen(true)}
            disabled={refreshingCache}
          >
            Refresh Pipeline
          </Button>
          <Button
            variant="outlined"
            startIcon={verifying ? <CircularProgress size={16} /> : <UserRoundSearch size={16} />}
            onClick={() => setVerificaDialogOpen(true)}
            disabled={verifying}
          >
            Verifica Clienti
          </Button>
          <Button
            variant="contained"
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <CheckCircle2 size={16} />}
            onClick={() => setImportDialogOpen(true)}
            disabled={importing}
          >
            Sincronizza da Funnelix
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <HeroKpiCard
            title="Contatti Totali"
            value={formatNumber(heroMetrics.totaleContatti)}
            subtitle="Contatti nel perimetro filtri"
            loading={heroLoading}
            icon={Users}
            iconBg="#eef2ff"
            iconColor="#4f46e5"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <HeroKpiCard
            title="Match Rate"
            value={formatPercent(heroMetrics.matchRate)}
            subtitle="Clienti trovati su contatti"
            loading={heroLoading}
            icon={UserCheck2}
            iconBg="#ecfdf3"
            iconColor="#059669"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <HeroKpiCard
            title="Error Rate Sync"
            value={formatPercent(heroMetrics.syncErrorRate)}
            subtitle="Errori su log sync recenti"
            loading={heroLoading}
            icon={AlertTriangle}
            iconBg="#fef2f2"
            iconColor="#dc2626"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <HeroKpiCard
            title="Valore Clienti"
            value={formatCurrency(heroMetrics.valoreClienti)}
            subtitle="Somma importo contratto"
            loading={heroLoading}
            icon={CircleDollarSign}
            iconBg="#eff6ff"
            iconColor="#2563eb"
          />
        </Grid>
      </Grid>

      <StyledCard>
        <CardContent>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
            <Tab value="contatti" label="Contatti" />
            <Tab value="log" label="Log Sync" />
            <Tab value="clienti_verificati" label={clientiVerificatiTabLabel} />
          </Tabs>

          {(activeTab === 'contatti' || activeTab === 'clienti_verificati') && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={isClientiVerificatiTab ? 8 : 11}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Cerca per nome, email, telefono, ID Funnelix..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={18} />
                        </InputAdornment>
                      ),
                      endAdornment: searchInput ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={(e) => clearFilterFieldFromEvent(e, 'search')}
                          >
                            <X size={14} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={isClientiVerificatiTab ? 4 : 1}
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    alignItems: 'center',
                    gap: 0.5,
                    flexWrap: { xs: 'wrap', md: 'nowrap' }
                  }}
                >
                  <Tooltip title="Mostra/Nascondi filtri">
                    <IconButton
                      size="small"
                      onClick={() => setShowFilters((prev) => !prev)}
                    >
                      <Badge
                        badgeContent={activeFiltersCount}
                        color="primary"
                        overlap="circular"
                        invisible={activeFiltersCount === 0}
                      >
                        <FilterAltIcon
                          fontSize="small"
                          color={showFilters ? 'primary' : 'inherit'}
                        />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Colonne visibili">
                    <IconButton
                      size="small"
                      onClick={(event) => setColumnsAnchorEl(event.currentTarget)}
                    >
                      <ViewColumnIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {isClientiVerificatiTab && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={
                        pushingClienti ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <ArrowUpRight size={14} />
                        )
                      }
                      onClick={() => setPushClientiDialogOpen(true)}
                      disabled={pushingClienti}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      Aggiorna Clienti su Funnelix
                    </Button>
                  )}
                  <Menu
                    anchorEl={columnsAnchorEl}
                    open={Boolean(columnsAnchorEl)}
                    onClose={() => setColumnsAnchorEl(null)}
                  >
                    <MenuItem
                      dense
                      onClick={() =>
                        setVisibleColumns((prev) => ({
                          ...prev,
                          funnelixContactId: !prev.funnelixContactId
                        }))
                      }
                    >
                      <FormControlLabel
                        control={<Checkbox checked={visibleColumns.funnelixContactId} />}
                        label="Funnelix Contact ID"
                      />
                    </MenuItem>
                    <MenuItem
                      dense
                      onClick={() =>
                        setVisibleColumns((prev) => ({
                          ...prev,
                          funnelixDateAdded: !prev.funnelixDateAdded
                        }))
                      }
                    >
                      <FormControlLabel
                        control={<Checkbox checked={visibleColumns.funnelixDateAdded} />}
                        label="Creato in Funnelix"
                      />
                    </MenuItem>
                    <MenuItem
                      dense
                      onClick={() =>
                        setVisibleColumns((prev) => ({
                          ...prev,
                          funnelixDateUpdated: !prev.funnelixDateUpdated
                        }))
                      }
                    >
                      <FormControlLabel
                        control={<Checkbox checked={visibleColumns.funnelixDateUpdated} />}
                        label="Aggiornato in Funnelix"
                      />
                    </MenuItem>
                  </Menu>
                </Grid>
                {showFilters && (
                  <>
                    {!isClientiVerificatiTab && (
                      <>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small" sx={{ position: 'relative' }}>
                            <InputLabel>Stato verifica</InputLabel>
                            <Select
                              value={filters.stato_verifica_cliente}
                              label="Stato verifica"
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  stato_verifica_cliente: e.target.value
                                }))
                              }
                            >
                              <MenuItem value="">Tutti</MenuItem>
                              <MenuItem value="da_verificare">Da verificare</MenuItem>
                              <MenuItem value="verificato_cliente">Verificato cliente</MenuItem>
                              <MenuItem value="verificato_non_cliente">Verificato non cliente</MenuItem>
                              <MenuItem value="errore">Errore</MenuItem>
                            </Select>
                            {filters.stato_verifica_cliente && (
                              <Tooltip title="Pulisci filtro">
                                <IconButton
                                  size="small"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => clearFilterFieldFromEvent(e, 'stato_verifica_cliente')}
                                  sx={{
                                    position: 'absolute',
                                    right: 32,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    p: 0.25
                                  }}
                                >
                                  <X size={14} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small" sx={{ position: 'relative' }}>
                            <InputLabel>Cliente trovato</InputLabel>
                            <Select
                              value={filters.cliente_trovato}
                              label="Cliente trovato"
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  cliente_trovato: e.target.value
                                }))
                              }
                            >
                              <MenuItem value="">Tutti</MenuItem>
                              <MenuItem value="1">Sì</MenuItem>
                              <MenuItem value="0">No</MenuItem>
                            </Select>
                            {filters.cliente_trovato !== '' && (
                              <Tooltip title="Pulisci filtro">
                                <IconButton
                                  size="small"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => clearFilterFieldFromEvent(e, 'cliente_trovato')}
                                  sx={{
                                    position: 'absolute',
                                    right: 32,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    p: 0.25
                                  }}
                                >
                                  <X size={14} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </FormControl>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small" sx={{ position: 'relative' }}>
                        <InputLabel>Pipeline</InputLabel>
                        <Select
                          value={filters.pipeline}
                          label="Pipeline"
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              pipeline: e.target.value,
                              stage: ''
                            }))
                          }
                        >
                          <MenuItem value="">Tutte</MenuItem>
                          {pipelineOptions.map((p) => (
                            <MenuItem key={p.funnelix_pipeline_id} value={p.funnelix_pipeline_id}>
                              {pipelineOptionLabel(p)}
                            </MenuItem>
                          ))}
                        </Select>
                        {filters.pipeline && (
                          <Tooltip title="Pulisci filtro">
                            <IconButton
                              size="small"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setFilters((prev) => ({
                                  ...prev,
                                  pipeline: '',
                                  stage: ''
                                }));
                                setPage(0);
                              }}
                              sx={{
                                position: 'absolute',
                                right: 32,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                p: 0.25
                              }}
                            >
                              <X size={14} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small" sx={{ position: 'relative' }}>
                        <InputLabel>Stage corrente</InputLabel>
                        <Select
                          value={filters.stage}
                          label="Stage corrente"
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              stage: e.target.value
                            }))
                          }
                        >
                          <MenuItem value="">Tutti</MenuItem>
                          {stageOptions.map((s) => (
                            <MenuItem key={`${s.funnelix_pipeline_id}-${s.funnelix_stage_id}`} value={s.funnelix_stage_id}>
                              {stageOptionLabel(s)}
                            </MenuItem>
                          ))}
                        </Select>
                        {filters.stage && (
                          <Tooltip title="Pulisci filtro">
                            <IconButton
                              size="small"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => clearFilterFieldFromEvent(e, 'stage')}
                              sx={{
                                position: 'absolute',
                                right: 32,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                p: 0.25
                              }}
                            >
                              <X size={14} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Data inserimento dal"
                        InputLabelProps={{ shrink: true }}
                        value={filters.data_inserimento_dal}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            data_inserimento_dal: normalizeDateInputToYmd(e.target.value)
                          }))
                        }
                        InputProps={{
                          endAdornment: filters.data_inserimento_dal ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={(e) => clearFilterFieldFromEvent(e, 'data_inserimento_dal')}
                              >
                                <X size={14} />
                              </IconButton>
                            </InputAdornment>
                          ) : null
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Data inserimento al"
                        InputLabelProps={{ shrink: true }}
                        value={filters.data_inserimento_al}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            data_inserimento_al: normalizeDateInputToYmd(e.target.value)
                          }))
                        }
                        InputProps={{
                          endAdornment: filters.data_inserimento_al ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={(e) => clearFilterFieldFromEvent(e, 'data_inserimento_al')}
                              >
                                <X size={14} />
                              </IconButton>
                            </InputAdornment>
                          ) : null
                        }}
                      />
                    </Grid>
                    {isClientiVerificatiTab && (
                      <>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Aggiornato su Funnelix dal"
                            InputLabelProps={{ shrink: true }}
                            value={filters.aggiornato_su_funnelix_dal}
                            onChange={(e) =>
                              setFilters((prev) => ({
                                ...prev,
                                aggiornato_su_funnelix_dal: normalizeDateInputToYmd(e.target.value)
                              }))
                            }
                            InputProps={{
                              endAdornment: filters.aggiornato_su_funnelix_dal ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => clearFilterFieldFromEvent(e, 'aggiornato_su_funnelix_dal')}
                                  >
                                    <X size={14} />
                                  </IconButton>
                                </InputAdornment>
                              ) : null
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Aggiornato su Funnelix al"
                            InputLabelProps={{ shrink: true }}
                            value={filters.aggiornato_su_funnelix_al}
                            onChange={(e) =>
                              setFilters((prev) => ({
                                ...prev,
                                aggiornato_su_funnelix_al: normalizeDateInputToYmd(e.target.value)
                              }))
                            }
                            InputProps={{
                              endAdornment: filters.aggiornato_su_funnelix_al ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => clearFilterFieldFromEvent(e, 'aggiornato_su_funnelix_al')}
                                  >
                                    <X size={14} />
                                  </IconButton>
                                </InputAdornment>
                              ) : null
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(filters.includi_mai_aggiornati_funnelix)}
                                onChange={(e) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    includi_mai_aggiornati_funnelix: e.target.checked
                                  }))
                                }
                              />
                            }
                            label="Includi anche mai aggiornati"
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Filtri attivi
                        </Typography>
                        {!isClientiVerificatiTab && filters.stato_verifica_cliente && (
                          <Chip
                            size="small"
                            label={
                              filters.stato_verifica_cliente === 'da_verificare'
                                ? 'Da verificare'
                                : filters.stato_verifica_cliente === 'verificato_cliente'
                                ? 'Verificato cliente'
                                : filters.stato_verifica_cliente === 'verificato_non_cliente'
                                ? 'Verificato non cliente'
                                : 'Errore'
                            }
                            onDelete={() => clearFilterField('stato_verifica_cliente')}
                          />
                        )}
                        {!isClientiVerificatiTab && filters.cliente_trovato !== '' && (
                          <Chip
                            size="small"
                            label={filters.cliente_trovato === '1' ? 'Cliente: Sì' : 'Cliente: No'}
                            onDelete={() => clearFilterField('cliente_trovato')}
                          />
                        )}
                        {filters.pipeline && (
                          <Chip
                            size="small"
                            label={`Pipeline: ${selectedPipelineLabel}`}
                            onDelete={() => {
                              setFilters((prev) => ({
                                ...prev,
                                pipeline: '',
                                stage: ''
                              }));
                              setPage(0);
                            }}
                          />
                        )}
                        {filters.stage && (
                          <Chip
                            size="small"
                            label={`Stage: ${selectedStageLabel}`}
                            onDelete={() => clearFilterField('stage')}
                          />
                        )}
                        {filters.data_inserimento_dal && (
                          <Chip
                            size="small"
                            label={`Da: ${formatYmdToIt(filters.data_inserimento_dal)}`}
                            onDelete={() => clearFilterField('data_inserimento_dal')}
                          />
                        )}
                        {filters.data_inserimento_al && (
                          <Chip
                            size="small"
                            label={`A: ${formatYmdToIt(filters.data_inserimento_al)}`}
                            onDelete={() => clearFilterField('data_inserimento_al')}
                          />
                        )}
                        {isClientiVerificatiTab && filters.aggiornato_su_funnelix_dal && (
                          <Chip
                            size="small"
                            label={`Agg. su Funnelix da: ${formatYmdToIt(filters.aggiornato_su_funnelix_dal)}`}
                            onDelete={() => clearFilterField('aggiornato_su_funnelix_dal')}
                          />
                        )}
                        {isClientiVerificatiTab && filters.aggiornato_su_funnelix_al && (
                          <Chip
                            size="small"
                            label={`Agg. su Funnelix a: ${formatYmdToIt(filters.aggiornato_su_funnelix_al)}`}
                            onDelete={() => clearFilterField('aggiornato_su_funnelix_al')}
                          />
                        )}
                        {isClientiVerificatiTab && filters.includi_mai_aggiornati_funnelix && (
                          <Chip
                            size="small"
                            label="Includi mai aggiornati"
                            onDelete={() => clearFilterField('includi_mai_aggiornati_funnelix')}
                          />
                        )}
                        {!activeFiltersCount && (
                          <Typography variant="caption" color="text.secondary">
                            Nessun filtro applicato
                          </Typography>
                        )}
                        {isClientiVerificatiTab && (
                          <Chip
                            size="small"
                            color="success"
                            label="Vista bloccata: Stato Verifica=Cliente, Cliente=Sì"
                          />
                        )}
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>

              <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: contactTableMinWidth }}>
                <TableHead>
                  <TableRow>
                    {visibleColumns.funnelixContactId && <TableCell>Funnelix Contact ID</TableCell>}
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'contatto' || sortBy === 'nome'}
                        direction={sortBy === 'contatto' || sortBy === 'nome' ? sortDir : 'asc'}
                        onClick={() => handleSort('contatto')}
                      >
                        Contatto
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'stato_verifica_cliente'}
                        direction={sortBy === 'stato_verifica_cliente' ? sortDir : 'asc'}
                        onClick={() => handleSort('stato_verifica_cliente')}
                      >
                        Stato Verifica
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'cliente_trovato'}
                        direction={sortBy === 'cliente_trovato' ? sortDir : 'asc'}
                        onClick={() => handleSort('cliente_trovato')}
                      >
                        Cliente
                      </TableSortLabel>
                    </TableCell>
                    {isClientiVerificatiTab && (
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'pipeline_nome'}
                          direction={sortBy === 'pipeline_nome' ? sortDir : 'asc'}
                          onClick={() => handleSort('pipeline_nome')}
                        >
                          Pipeline Corrente
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {isClientiVerificatiTab && (
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'stage_nome'}
                          direction={sortBy === 'stage_nome' ? sortDir : 'asc'}
                          onClick={() => handleSort('stage_nome')}
                        >
                          Stage Corrente
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {isClientiVerificatiTab && (
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'importo_contratto'}
                          direction={sortBy === 'importo_contratto' ? sortDir : 'asc'}
                          onClick={() => handleSort('importo_contratto')}
                        >
                          Importo Contratto
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {isClientiVerificatiTab && (
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'aggiornato_su_funnelix_il'}
                          direction={sortBy === 'aggiornato_su_funnelix_il' ? sortDir : 'asc'}
                          onClick={() => handleSort('aggiornato_su_funnelix_il')}
                        >
                          Aggiornato su Funnelix
                        </TableSortLabel>
                      </TableCell>
                    )}
                    {!isClientiVerificatiTab && (
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'pipeline_nome'}
                        direction={sortBy === 'pipeline_nome' ? sortDir : 'asc'}
                        onClick={() => handleSort('pipeline_nome')}
                      >
                        Pipeline Corrente
                      </TableSortLabel>
                    </TableCell>
                    )}
                    {!isClientiVerificatiTab && (
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'stage_nome'}
                        direction={sortBy === 'stage_nome' ? sortDir : 'asc'}
                        onClick={() => handleSort('stage_nome')}
                      >
                        Stage Corrente
                      </TableSortLabel>
                    </TableCell>
                    )}
                    {!isClientiVerificatiTab && (
                    <TableCell>
                      <Tooltip title="Ultima data attività del contatto ricavata dal payload Funnelix">
                        <TableSortLabel
                          active={sortBy === 'data_contatto'}
                          direction={sortBy === 'data_contatto' ? sortDir : 'asc'}
                          onClick={() => handleSort('data_contatto')}
                        >
                          Ultima attività
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    )}
                    {!isClientiVerificatiTab && visibleColumns.funnelixDateAdded && (
                      <TableCell>
                        <Tooltip title="Data creazione contatto su Funnelix (dateAdded)">
                          <TableSortLabel
                            active={sortBy === 'funnelix_date_added'}
                            direction={sortBy === 'funnelix_date_added' ? sortDir : 'asc'}
                            onClick={() => handleSort('funnelix_date_added')}
                          >
                            Creato In Funnelix
                          </TableSortLabel>
                        </Tooltip>
                      </TableCell>
                    )}
                    {!isClientiVerificatiTab && visibleColumns.funnelixDateUpdated && (
                      <TableCell>
                        <Tooltip title="Data ultimo aggiornamento contatto su Funnelix (dateUpdated)">
                          <TableSortLabel
                            active={sortBy === 'funnelix_date_updated'}
                            direction={sortBy === 'funnelix_date_updated' ? sortDir : 'asc'}
                            onClick={() => handleSort('funnelix_date_updated')}
                          >
                            Aggiornato In Funnelix
                          </TableSortLabel>
                        </Tooltip>
                      </TableCell>
                    )}
                    {!isClientiVerificatiTab && (
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'importo_contratto'}
                        direction={sortBy === 'importo_contratto' ? sortDir : 'asc'}
                        onClick={() => handleSort('importo_contratto')}
                      >
                        Importo Contratto
                      </TableSortLabel>
                    </TableCell>
                    )}
                    {!isClientiVerificatiTab && (
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'ultimo_sync'}
                        direction={sortBy === 'ultimo_sync' ? sortDir : 'asc'}
                        onClick={() => handleSort('ultimo_sync')}
                      >
                        Ultimo Sync
                      </TableSortLabel>
                    </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableColumnCount} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColumnCount} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nessun contatto importato trovato
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id} hover>
                        {visibleColumns.funnelixContactId && <TableCell>{row.funnelix_contact_id || '-'}</TableCell>}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: 'rgba(37, 99, 235, 0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mt: 0.3
                              }}
                            >
                              <PersonOutlineIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>{row.nome || '-'}</Typography>
                              {row.telefono ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mt: 0.25
                                  }}
                                >
                                  <Phone size={14} color="#64748b" />
                                  <Typography variant="body2" color="text.secondary">
                                    {row.telefono}
                                  </Typography>
                                </Box>
                              ) : null}
                              {row.email ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                  {row.email}
                                </Typography>
                              ) : null}
                              {!row.telefono && !row.email ? (
                                <Typography variant="body2" color="text.disabled" sx={{ mt: 0.25 }}>
                                  Nessun recapito
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{getStatoChip(row.stato_verifica_cliente)}</TableCell>
                        <TableCell>{row.cliente_trovato ? 'Sì' : 'No'}</TableCell>
                        {isClientiVerificatiTab && (
                          <TableCell>{row.pipeline_nome || row.funnelix_pipeline_id || '-'}</TableCell>
                        )}
                        {isClientiVerificatiTab && (
                          <TableCell>{row.stage_nome || row.funnelix_stage_id || '-'}</TableCell>
                        )}
                        {isClientiVerificatiTab && (
                          <TableCell>
                            {row.importo_contratto !== null && row.importo_contratto !== undefined ? (
                              Number(row.importo_contratto) > 0 ? (
                                <Chip
                                  size="small"
                                  color="success"
                                  label={`${Number(row.importo_contratto).toFixed(2)} ${row.valuta || 'EUR'}`}
                                />
                              ) : (
                                `${Number(row.importo_contratto).toFixed(2)} ${row.valuta || 'EUR'}`
                              )
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        )}
                        {isClientiVerificatiTab && (
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {row.aggiornato_su_funnelix_il ? (
                              <Tooltip
                                title={
                                  `${formatDateTimeRome(row.aggiornato_su_funnelix_il)}${
                                    row.aggiornato_su_funnelix_da_nome_utente
                                      ? ` • da ${row.aggiornato_su_funnelix_da_nome_utente}`
                                      : ''
                                  }`
                                }
                              >
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatDateTimeShortRome(row.aggiornato_su_funnelix_il)}
                                  </Typography>
                                  {row.aggiornato_su_funnelix_da_nome_utente ? (
                                    <Typography variant="caption" color="text.secondary">
                                      da {row.aggiornato_su_funnelix_da_nome_utente}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        {!isClientiVerificatiTab && (
                          <TableCell>{row.pipeline_nome || row.funnelix_pipeline_id || '-'}</TableCell>
                        )}
                        {!isClientiVerificatiTab && (
                          <TableCell>{row.stage_nome || row.funnelix_stage_id || '-'}</TableCell>
                        )}
                        {!isClientiVerificatiTab && (
                          <TableCell>{formatDateTime(row.data_contatto)}</TableCell>
                        )}
                        {!isClientiVerificatiTab && visibleColumns.funnelixDateAdded && (
                          <TableCell>{formatDateTime(row.funnelix_date_added)}</TableCell>
                        )}
                        {!isClientiVerificatiTab && visibleColumns.funnelixDateUpdated && (
                          <TableCell>{formatDateTime(row.funnelix_date_updated)}</TableCell>
                        )}
                        {!isClientiVerificatiTab && (
                        <TableCell>
                          {row.importo_contratto !== null && row.importo_contratto !== undefined ? (
                            Number(row.importo_contratto) > 0 ? (
                              <Chip
                                size="small"
                                color="success"
                                label={`${Number(row.importo_contratto).toFixed(2)} ${row.valuta || 'EUR'}`}
                              />
                            ) : (
                              `${Number(row.importo_contratto).toFixed(2)} ${row.valuta || 'EUR'}`
                            )
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        )}
                        {!isClientiVerificatiTab && (
                          <TableCell>{formatDateTime(row.ultimo_sync || row.aggiornato_il)}</TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </Box>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Righe per pagina"
              />
            </>
          )}

          {activeTab === 'log' && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Cerca per correlation id, azione, endpoint, errore..."
                    value={logsFilters.search}
                    onChange={(e) =>
                      setLogsFilters((prev) => ({
                        ...prev,
                        search: e.target.value
                      }))
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={18} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Esito</InputLabel>
                    <Select
                      value={logsFilters.esito}
                      label="Esito"
                      onChange={(e) =>
                        setLogsFilters((prev) => ({
                          ...prev,
                          esito: e.target.value
                        }))
                      }
                    >
                      <MenuItem value="">Tutti</MenuItem>
                      <MenuItem value="ok">OK</MenuItem>
                      <MenuItem value="errore">Errore</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Entità</InputLabel>
                    <Select
                      value={logsFilters.entita_tipo}
                      label="Entità"
                      onChange={(e) =>
                        setLogsFilters((prev) => ({
                          ...prev,
                          entita_tipo: e.target.value
                        }))
                      }
                    >
                      <MenuItem value="">Tutte</MenuItem>
                      <MenuItem value="contatto">Contatto</MenuItem>
                      <MenuItem value="opportunita">Opportunità</MenuItem>
                      <MenuItem value="pipeline">Pipeline</MenuItem>
                      <MenuItem value="stage">Stage</MenuItem>
                      <MenuItem value="verifica_cliente">Verifica Cliente</MenuItem>
                      <MenuItem value="generico">Generico</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Entità</TableCell>
                    <TableCell>Azione</TableCell>
                    <TableCell>Endpoint</TableCell>
                    <TableCell>HTTP</TableCell>
                    <TableCell>Esito</TableCell>
                    <TableCell>Errore</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logsLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : logRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nessun log sincronizzazione trovato
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logRows.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>{formatDateTime(log.eseguito_il)}</TableCell>
                        <TableCell>{log.entita_tipo || '-'}</TableCell>
                        <TableCell>{log.azione || '-'}</TableCell>
                        <TableCell>{log.endpoint || '-'}</TableCell>
                        <TableCell>{log.http_status || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={log.esito === 'ok' ? 'success' : 'error'}
                            label={log.esito || '-'}
                          />
                        </TableCell>
                        <TableCell>{log.errore_sync || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={logsTotal}
                page={logsPage}
                onPageChange={(_, newPage) => setLogsPage(newPage)}
                rowsPerPage={logsRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setLogsRowsPerPage(parseInt(event.target.value, 10));
                  setLogsPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Righe per pagina"
              />
            </>
          )}
        </CardContent>
      </StyledCard>

      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importa Contatti Da Funnelix</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl component="fieldset">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Modalità import
              </Typography>
              <RadioGroup
                value={importMode}
                onChange={(e) => setImportMode(e.target.value)}
              >
                <FormControlLabel
                  value="incrementale"
                  control={<Radio />}
                  label="Incrementale (dall'ultimo import riuscito)"
                />
                <FormControlLabel
                  value="intervallo"
                  control={<Radio />}
                  label="Intervallo date (manuale)"
                />
              </RadioGroup>
            </FormControl>

            {importMode === 'intervallo' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Preset rapido</InputLabel>
                    <Select
                      value={importPreset}
                      label="Preset rapido"
                      onChange={(e) => setImportPreset(e.target.value)}
                    >
                      <MenuItem value="oggi">Oggi</MenuItem>
                      <MenuItem value="ultimi_7_giorni">Ultimi 7 giorni</MenuItem>
                      <MenuItem value="ultimo_mese">Ultimo mese</MenuItem>
                      <MenuItem value="custom">Personalizzato</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Data dal"
                    InputLabelProps={{ shrink: true }}
                    value={importDateFrom}
                    onChange={(e) => {
                      setImportPreset('custom');
                      setImportDateFrom(e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Data al"
                    InputLabelProps={{ shrink: true }}
                    value={importDateTo}
                    onChange={(e) => {
                      setImportPreset('custom');
                      setImportDateTo(e.target.value);
                    }}
                  />
                </Grid>
              </Grid>
            )}

            <Typography variant="body2" color="text.secondary">
              Il backend recupera automaticamente tutti i contatti disponibili per la modalità selezionata.
            </Typography>

            <Box
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                p: 1.5,
                bgcolor: '#f8fafc'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Incrementale da: {incrementaleDaLabel}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75 }}>
                Intervallo effettivo: {intervalloEffettivoLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                Timezone riferimento: Europe/Rome. Date e orari mostrati in formato italiano.
              </Typography>
              <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px dashed #d1d5db' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Contatti disponibili stimati:{' '}
                  {estimateLoading
                    ? 'calcolo in corso...'
                    : estimatedContacts == null
                      ? '-'
                      : formatNumber(estimatedContacts)}
                </Typography>
                {!!estimateError && (
                  <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.25 }}>
                    {estimateError}
                  </Typography>
                )}
                {!estimateError && estimateFromAt && estimateToAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    Finestra stimata: {formatDateTimeRome(estimateFromAt)} - {formatDateTimeRome(estimateToAt)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={importing}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleConfirmImport} disabled={importing}>
            {importing ? 'Import in corso...' : 'Avvia Import'}
          </Button>
        </DialogActions>
        {importing && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Job: {importJobId || '-'} | Stato: {importJobState || 'in attesa'} | Progresso:{' '}
              {importJobProgress}%
            </Typography>
          </Box>
        )}
        {!importing && importJobError && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Typography variant="body2" color="error.main">
              {importJobError}
            </Typography>
          </Box>
        )}
      </Dialog>

      <Dialog
        open={refreshDialogOpen}
        onClose={() => {
          if (refreshingCache) return;
          setRefreshDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Refresh Pipeline Funnelix</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Typography variant="body2">
              Questa operazione sincronizza la struttura commerciale da Funnelix alla cache locale.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <CheckCircle2 size={16} color="#16a34a" />
              <Typography variant="subtitle2">Cosa aggiorna</Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Pipeline attive disponibili in Funnelix
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Stage associati a ciascuna pipeline
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Metadati usati nei filtri e nelle colonne della dashboard
                </Typography>
              </li>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Info size={16} color="#2563eb" />
              <Typography variant="subtitle2">Quando usarlo</Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Dopo modifiche in Funnelix (nuove pipeline/stage, rinomina, riordino).
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Se nei filtri vedi nomi vecchi o ID tecnici di pipeline/stage.
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Non modifica i contatti importati: aggiorna solo cache pipeline/stage.
                </Typography>
              </li>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRefreshDialogOpen(false)}
            disabled={refreshingCache}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setRefreshDialogOpen(false);
              await executeRefreshCache();
            }}
            disabled={refreshingCache}
          >
            {refreshingCache ? 'Aggiornamento...' : 'Conferma e Aggiorna'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pushClientiDialogOpen}
        onClose={() => {
          if (pushingClienti) return;
          setPushClientiDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Aggiorna Clienti su Funnelix</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Aggiorna Funnelix per i record nella tab “Clienti Verificati” (rispettando i filtri correnti).
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
            <Chip
              size="small"
              color="primary"
              label="Target Pipeline: vendita"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label="Target Stage: cliente"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label="Valore: importo contratto"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              size="small"
              color="default"
              label={`Clienti nel perimetro: ${loading ? '-' : formatNumber(total)}`}
              sx={{ fontWeight: 700 }}
            />
          </Box>

          <Typography
            variant="body2"
            sx={{ color: 'error.main', fontWeight: 800, mb: 1.75 }}
          >
            Ultimo aggiornamento su Funnelix:{' '}
            {pushClientiLastUpdateLoading
              ? 'caricamento...'
              : pushClientiLastUpdate.aggiornato_su_funnelix_il
              ? `${formatDateTime(pushClientiLastUpdate.aggiornato_su_funnelix_il)}${
                  pushClientiLastUpdate.aggiornato_su_funnelix_da_nome_utente
                    ? ` • da ${pushClientiLastUpdate.aggiornato_su_funnelix_da_nome_utente}`
                    : ''
                }`
              : '-'}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: alpha('#2563eb', 0.12),
                  color: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <FolderKanban size={18} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Spostamento pipeline/stage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Per ogni contatto verificato come Cliente, sposta l’opportunità nella pipeline “vendita” e nello stage “cliente”.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: alpha('#16a34a', 0.12),
                  color: '#16a34a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <CircleDollarSign size={18} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Valore opportunità
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Imposta il valore economico dell’opportunità usando “Importo contratto” calcolato dalla verifica.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: alpha('#0f172a', 0.08),
                  color: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Info size={18} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Nota operativa
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  L’operazione modifica Funnelix. Se la pipeline/stage non esistono o sono rinominati, esegui prima “Refresh Pipeline”.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Filtri applicati
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {filters.search ? <Chip size="small" label={`Ricerca: ${filters.search}`} /> : null}
              {filters.pipeline ? <Chip size="small" label={`Pipeline: ${selectedPipelineLabel}`} /> : null}
              {filters.stage ? <Chip size="small" label={`Stage: ${selectedStageLabel}`} /> : null}
              {filters.data_inserimento_dal ? (
                <Chip size="small" label={`Da: ${formatYmdToIt(filters.data_inserimento_dal)}`} />
              ) : null}
              {filters.data_inserimento_al ? (
                <Chip size="small" label={`A: ${formatYmdToIt(filters.data_inserimento_al)}`} />
              ) : null}
              <Chip size="small" color="success" label="Vista: Clienti Verificati" />
              {!activeFiltersCount && !filters.search ? (
                <Chip size="small" label="Nessun filtro applicato" />
              ) : null}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPushClientiDialogOpen(false)} disabled={pushingClienti}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={executePushClienti}
            disabled={pushingClienti}
            startIcon={pushingClienti ? <CircularProgress size={16} color="inherit" /> : <ArrowUpRight size={16} />}
          >
            {pushingClienti ? 'Aggiornamento...' : 'Avvia aggiornamento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={verificaDialogOpen}
        onClose={() => {
          if (verifying) return;
          setVerificaDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verifica Clienti</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Typography variant="body2">
              Questa operazione controlla se i contatti importati sono gi&agrave; presenti come clienti nel sistema.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <CheckCircle2 size={16} color="#16a34a" />
              <Typography variant="subtitle2">Cosa verifica</Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Match anagrafico su email, telefono, codice fiscale e partita IVA
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Presenza del contatto nelle fonti clienti configurate
                </Typography>
              </li>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <CheckCircle2 size={16} color="#16a34a" />
              <Typography variant="subtitle2">Cosa aggiorna</Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Stato verifica: Da verificare, Cliente, Non cliente, Errore
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Campo Cliente trovato e, se disponibili, informazioni economiche
                </Typography>
              </li>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setVerificaDialogOpen(false)}
            disabled={verifying}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setVerificaDialogOpen(false);
              await executeVerifica();
            }}
            disabled={verifying}
          >
            {verifying ? 'Verifica...' : 'Conferma e Verifica'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
